import * as React from "react";
import { useEffect, useMemo, useState } from "react";

import { flattenDeep } from "lodash";

import { Report } from "services/types/reports.type";

import {
  PureToneEarsData,
  PureToneEarsDataHidden,
  PureToneReportValues
} from "./pure-tone-audiogram.type";
import useHideUltraHighFrequencies from "./use-hide-ultra-high-frequencies.hook";
import { getPureToneDefaultRowArray } from "./utils";

export type Ear = "left" | "right";

const PureToneAudioGramContext = React.createContext<any>(null);

type PureToneAudioGramProviderProps = {
  children?: React.ReactNode;
  currentReport?: Report | null;
  isLoadingReport?: boolean;
  // true in header/drawer mini-previews to always show ultra-high frequencies
  // and skip the per-chart provider-preference fetch.
  alwaysShowUltraHigh?: boolean;
};

//PureToneEarsData
const getDefaultEarsData = (): PureToneEarsData => ({
  left: {
    airConduction: getPureToneDefaultRowArray(),
    airConductionAnnotation: getPureToneDefaultRowArray(),

    airConductionMaskingLevel: getPureToneDefaultRowArray(),
    airConductionMaskingLevelAnnotation: getPureToneDefaultRowArray(),

    boneConduction: getPureToneDefaultRowArray(),
    boneConductionAnnotation: getPureToneDefaultRowArray(),

    boneConductionMaskingLevel: getPureToneDefaultRowArray(),
    boneConductionMaskingLevelAnnotation: getPureToneDefaultRowArray(),

    mcl: getPureToneDefaultRowArray(),
    mclAnnotation: getPureToneDefaultRowArray(),

    ucl: getPureToneDefaultRowArray(),
    uclAnnotation: getPureToneDefaultRowArray(),

    soundfieldAided: getPureToneDefaultRowArray(),
    soundfieldAidedAnnotation: getPureToneDefaultRowArray(),

    soundfieldUnaided: getPureToneDefaultRowArray(),
    soundfieldUnaidedAnnotation: getPureToneDefaultRowArray()
  },
  right: {
    airConduction: getPureToneDefaultRowArray(),
    airConductionAnnotation: getPureToneDefaultRowArray(),

    airConductionMaskingLevel: getPureToneDefaultRowArray(),
    airConductionMaskingLevelAnnotation: getPureToneDefaultRowArray(),

    boneConduction: getPureToneDefaultRowArray(),
    boneConductionAnnotation: getPureToneDefaultRowArray(),

    boneConductionMaskingLevel: getPureToneDefaultRowArray(),
    boneConductionMaskingLevelAnnotation: getPureToneDefaultRowArray(),

    mcl: getPureToneDefaultRowArray(),
    mclAnnotation: getPureToneDefaultRowArray(),

    ucl: getPureToneDefaultRowArray(),
    uclAnnotation: getPureToneDefaultRowArray(),

    soundfieldAided: getPureToneDefaultRowArray(),
    soundfieldAidedAnnotation: getPureToneDefaultRowArray(),

    soundfieldUnaided: getPureToneDefaultRowArray(),
    soundfieldUnaidedAnnotation: getPureToneDefaultRowArray()
  }
});

const getDefaultHiddenData = () => ({
  left: {
    airConduction: false,
    airConductionMaskingLevel: false,
    boneConduction: false,
    boneConductionMaskingLevel: false,
    mcl: false,
    ucl: false,
    soundfieldAided: false,
    soundfieldUnaided: false
  },
  right: {
    airConduction: false,
    airConductionMaskingLevel: false,
    boneConduction: false,
    boneConductionMaskingLevel: false,
    mcl: false,
    ucl: false,
    soundfieldAided: false,
    soundfieldUnaided: false
  }
});

export function PureToneAudioGramProvider({
  children,
  currentReport,
  isLoadingReport = false,
  alwaysShowUltraHigh = false
}: PureToneAudioGramProviderProps) {
  const [isMergedView, setIsMergedView] = useState(true);
  const [showOverlay, setShowOverlay] = useState(false);
  // Opt-in per report and never persisted -- there is no user preference or
  // backend field behind it, so every report starts with it off.
  const [showSpeechBanana, setShowSpeechBanana] = useState(false);

  const [earsData, setEarsData] =
    useState<PureToneEarsData>(getDefaultEarsData);

  const selectedReport = currentReport;
  const isLoadingSelectedReport = isLoadingReport;
  const hideUltraHigh = useHideUltraHighFrequencies(currentReport, {
    skip: alwaysShowUltraHigh
  });

  const [hiddenData, setHiddenData] = useState<PureToneEarsDataHidden>(
    getDefaultHiddenData()
  );

  const reportFormData: PureToneReportValues | {} = useMemo(() => {
    const { pureToneAudiogramReport } = selectedReport || {};

    if (
      pureToneAudiogramReport !== null &&
      pureToneAudiogramReport !== undefined
    ) {
      const result = {
        isSelected: pureToneAudiogramReport.isSelected,
        audiogramAnnotation: pureToneAudiogramReport.audiogramAnnotation ?? "",
        left: {
          airConduction: {
            isSelected: pureToneAudiogramReport.isAirConductionLSelected,
            numerics:
              pureToneAudiogramReport.airConductionL ??
              getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.airConductionLAnnotation ??
              getPureToneDefaultRowArray()
          },
          airConductionMaskingLevel: {
            isSelected:
              pureToneAudiogramReport.isAirConductionMaskingLevelLSelected,
            numerics:
              pureToneAudiogramReport.airConductionMaskingLevelL ??
              getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.airConductionMaskingLevelLAnnotation ??
              getPureToneDefaultRowArray()
          },
          boneConduction: {
            isSelected: pureToneAudiogramReport.isBoneConductionLSelected,
            numerics:
              pureToneAudiogramReport.boneConductionL ??
              getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.boneConductionLAnnotation ??
              getPureToneDefaultRowArray()
          },
          boneConductionMaskingLevel: {
            isSelected:
              pureToneAudiogramReport.isBoneConductionMaskingLevelLSelected,
            numerics:
              pureToneAudiogramReport.boneConductionMaskingLevelL ??
              getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.boneConductionMaskingLevelLAnnotation ??
              getPureToneDefaultRowArray()
          },
          mcl: {
            isSelected: pureToneAudiogramReport.isMclLSelected,
            numerics:
              pureToneAudiogramReport.mclL ?? getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.mclLAnnotation ??
              getPureToneDefaultRowArray()
          },
          ucl: {
            isSelected: pureToneAudiogramReport.isUclLSelected,
            numerics:
              pureToneAudiogramReport.uclL ?? getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.uclLAnnotation ??
              getPureToneDefaultRowArray()
          },
          soundfieldAided: {
            isSelected: pureToneAudiogramReport.isSoundFieldAidedLSelected,
            numerics:
              pureToneAudiogramReport.soundfieldAidedL ??
              getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.soundfieldAidedLAnnotation ??
              getPureToneDefaultRowArray()
          },
          soundfieldUnaided: {
            isSelected: pureToneAudiogramReport.isSoundFieldUnaidedLSelected,
            numerics:
              pureToneAudiogramReport.soundfieldUnaidedL ??
              getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.soundfieldUnaidedLAnnotation ??
              getPureToneDefaultRowArray()
          }
        },
        right: {
          airConduction: {
            isSelected: pureToneAudiogramReport.isAirConductionRSelected,
            numerics:
              pureToneAudiogramReport.airConductionR ??
              getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.airConductionRAnnotation ??
              getPureToneDefaultRowArray()
          },
          airConductionMaskingLevel: {
            isSelected:
              pureToneAudiogramReport.isAirConductionMaskingLevelRSelected,
            numerics:
              pureToneAudiogramReport.airConductionMaskingLevelR ??
              getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.airConductionMaskingLevelRAnnotation ??
              getPureToneDefaultRowArray()
          },
          boneConduction: {
            isSelected: pureToneAudiogramReport.isBoneConductionRSelected,
            numerics:
              pureToneAudiogramReport.boneConductionR ??
              getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.boneConductionRAnnotation ??
              getPureToneDefaultRowArray()
          },
          boneConductionMaskingLevel: {
            isSelected:
              pureToneAudiogramReport.isBoneConductionMaskingLevelRSelected,
            numerics:
              pureToneAudiogramReport.boneConductionMaskingLevelR ??
              getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.boneConductionMaskingLevelRAnnotation ??
              getPureToneDefaultRowArray()
          },
          mcl: {
            isSelected: pureToneAudiogramReport.isMclRSelected,
            numerics:
              pureToneAudiogramReport.mclR ?? getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.mclRAnnotation ??
              getPureToneDefaultRowArray()
          },
          ucl: {
            isSelected: pureToneAudiogramReport.isUclRSelected,
            numerics:
              pureToneAudiogramReport.uclR ?? getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.uclRAnnotation ??
              getPureToneDefaultRowArray()
          },
          soundfieldAided: {
            isSelected: pureToneAudiogramReport.isSoundFieldAidedRSelected,
            numerics:
              pureToneAudiogramReport.soundfieldAidedR ??
              getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.soundfieldAidedRAnnotation ??
              getPureToneDefaultRowArray()
          },
          soundfieldUnaided: {
            isSelected: pureToneAudiogramReport.isSoundFieldUnaidedRSelected,
            numerics:
              pureToneAudiogramReport.soundfieldUnaidedR ??
              getPureToneDefaultRowArray(),
            values:
              pureToneAudiogramReport.soundfieldUnaidedRAnnotation ??
              getPureToneDefaultRowArray()
          }
        }
      };

      return result;
    }
    return {};
  }, [selectedReport]);

  useEffect(() => {
    const { pureToneAudiogramReport } = selectedReport || {};
    if (
      !isLoadingSelectedReport &&
      pureToneAudiogramReport !== null &&
      pureToneAudiogramReport !== undefined
    ) {
      setEarsData({
        left: {
          airConduction: pureToneAudiogramReport.airConductionL,
          airConductionAnnotation:
            pureToneAudiogramReport.airConductionLAnnotation,

          airConductionMaskingLevel:
            pureToneAudiogramReport.airConductionMaskingLevelL,
          airConductionMaskingLevelAnnotation:
            pureToneAudiogramReport.airConductionMaskingLevelLAnnotation,

          boneConduction: pureToneAudiogramReport.boneConductionL,
          boneConductionAnnotation:
            pureToneAudiogramReport.boneConductionLAnnotation,

          boneConductionMaskingLevel:
            pureToneAudiogramReport.boneConductionMaskingLevelL,
          boneConductionMaskingLevelAnnotation:
            pureToneAudiogramReport.boneConductionMaskingLevelLAnnotation,

          mcl: pureToneAudiogramReport.mclL,
          mclAnnotation: pureToneAudiogramReport.mclLAnnotation,

          ucl: pureToneAudiogramReport.uclL,
          uclAnnotation: pureToneAudiogramReport.uclLAnnotation,

          soundfieldAided: pureToneAudiogramReport.soundfieldAidedL,
          soundfieldAidedAnnotation:
            pureToneAudiogramReport.soundfieldAidedLAnnotation,

          soundfieldUnaided: pureToneAudiogramReport.soundfieldUnaidedL,
          soundfieldUnaidedAnnotation:
            pureToneAudiogramReport.soundfieldUnaidedLAnnotation
        },
        right: {
          airConduction: pureToneAudiogramReport.airConductionR,
          airConductionAnnotation:
            pureToneAudiogramReport.airConductionRAnnotation,

          airConductionMaskingLevel:
            pureToneAudiogramReport.airConductionMaskingLevelR,
          airConductionMaskingLevelAnnotation:
            pureToneAudiogramReport.airConductionMaskingLevelRAnnotation,

          boneConduction: pureToneAudiogramReport.boneConductionR,
          boneConductionAnnotation:
            pureToneAudiogramReport.boneConductionRAnnotation,

          boneConductionMaskingLevel:
            pureToneAudiogramReport.boneConductionMaskingLevelR,
          boneConductionMaskingLevelAnnotation:
            pureToneAudiogramReport.boneConductionMaskingLevelRAnnotation,

          mcl: pureToneAudiogramReport.mclR,
          mclAnnotation: pureToneAudiogramReport.mclRAnnotation,

          ucl: pureToneAudiogramReport.uclR,
          uclAnnotation: pureToneAudiogramReport.uclRAnnotation,

          soundfieldAided: pureToneAudiogramReport.soundfieldAidedR,
          soundfieldAidedAnnotation:
            pureToneAudiogramReport.soundfieldAidedRAnnotation,

          soundfieldUnaided: pureToneAudiogramReport.soundfieldUnaidedR,
          soundfieldUnaidedAnnotation:
            pureToneAudiogramReport.soundfieldUnaidedRAnnotation
        }
      });
      setHiddenData({
        left: {
          airConduction: !pureToneAudiogramReport.isAirConductionLSelected,
          airConductionMaskingLevel:
            !pureToneAudiogramReport.isAirConductionMaskingLevelLSelected,
          boneConduction: !pureToneAudiogramReport.isBoneConductionLSelected,
          boneConductionMaskingLevel:
            !pureToneAudiogramReport.isBoneConductionMaskingLevelLSelected,
          mcl: !pureToneAudiogramReport.isMclLSelected,
          ucl: !pureToneAudiogramReport.isUclLSelected,
          soundfieldAided: !pureToneAudiogramReport.isSoundFieldAidedLSelected,
          soundfieldUnaided:
            !pureToneAudiogramReport.isSoundFieldUnaidedLSelected
        },
        right: {
          airConduction: !pureToneAudiogramReport.isAirConductionRSelected,
          airConductionMaskingLevel:
            !pureToneAudiogramReport.isAirConductionMaskingLevelRSelected,
          boneConduction: !pureToneAudiogramReport.isBoneConductionRSelected,
          boneConductionMaskingLevel:
            !pureToneAudiogramReport.isBoneConductionMaskingLevelRSelected,
          mcl: !pureToneAudiogramReport.isMclRSelected,
          ucl: !pureToneAudiogramReport.isUclRSelected,
          soundfieldAided: !pureToneAudiogramReport.isSoundFieldAidedRSelected,
          soundfieldUnaided:
            !pureToneAudiogramReport.isSoundFieldUnaidedRSelected
        }
      });
    } else if (
      pureToneAudiogramReport === null ||
      pureToneAudiogramReport === undefined
    ) {
      setEarsData(getDefaultEarsData());
      setHiddenData(getDefaultHiddenData());
    }
  }, [selectedReport, isLoadingSelectedReport]);

  const hasAudiogramData = useMemo(() => {
    const earsDataValues = flattenDeep([
      ...Object.values(earsData.left),
      ...Object.values(earsData.right)
    ]).filter((value) => value !== "");
    return Boolean(earsDataValues.some((value) => value !== null));
  }, [earsData]);

  return (
    <PureToneAudioGramContext.Provider
      value={{
        earsData,
        setEarsData,
        hiddenData,
        setHiddenData,
        reportFormData,
        isMergedView,
        setIsMergedView,
        hasAudiogramData,
        showOverlay,
        setShowOverlay,
        showSpeechBanana,
        setShowSpeechBanana,
        hideUltraHigh
      }}
    >
      {children}
    </PureToneAudioGramContext.Provider>
  );
}

export default function usePureToneAudiogram() {
  const PTAudiogramContext = React.useContext(PureToneAudioGramContext);
  if (!PTAudiogramContext) {
    throw new Error(
      `usePureToneAudiogram must be used within a PureToneAudioGramContext`
    );
  }
  return PTAudiogramContext;
}
